import { httpClient } from '../api/http-client';

export interface Permission {
  id: string;
  nombre: string;
  descripcion?: string;
  activo: boolean;
}

export interface Module {
  id: string;
  nombre: string;
  descripcion?: string;
  ruta: string;
  icono: string;
  orden: number;
  tipo: string;
  moduloPadreId?: string;
  activo: boolean;
  permisos: Permission[];
}

export interface Role {
  id: string;
  nombre: string;
  descripcion?: string;
  activo: boolean;
  creadoEn: string;
  actualizadoEn: string;
  modulos?: Module[];
}

export interface CreateRoleDto {
  nombre: string;
  descripcion?: string;
  moduloIds?: string[];
}

export interface UpdateRoleDto {
  nombre?: string;
  descripcion?: string;
  activo?: boolean;
  moduloIds?: string[];
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface PaginatedRolesResponse {
  data: Role[];
  meta: PaginationMeta;
}

export class RolesService {
  static async getRoles(
    page: number = 1, 
    limit: number = 10, 
    activo?: boolean
  ): Promise<PaginatedRolesResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (activo !== undefined) {
        params.append('activo', activo.toString());
      }

      const response = await httpClient.get<{ data: PaginatedRolesResponse }>(
        `/auth/roles?${params}`
      );
      
      return response.data.data || { data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 0, hasPrevious: false, hasNext: false } };
    } catch (error) {
      console.error('Error fetching roles:', error);
      return { data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 0, hasPrevious: false, hasNext: false } };
    }
  }

  static async searchRoles(
    searchTerm: string,
    page: number = 1, 
    limit: number = 10, 
    activo?: boolean
  ): Promise<PaginatedRolesResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: searchTerm
      });

      if (activo !== undefined) {
        params.append('activo', activo.toString());
      }

      const response = await httpClient.get<{ data: PaginatedRolesResponse }>(
        `/auth/roles?${params}`
      );
      
      return response.data.data || { data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 0, hasPrevious: false, hasNext: false } };
    } catch (error) {
      console.error('Error searching roles:', error);
      return { data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 0, hasPrevious: false, hasNext: false } };
    }
  }

  static async getRoleById(id: string): Promise<Role> {
    const response = await httpClient.get<Role>(
      `/auth/roles/${id}`
    );
    
    return (response.data as any).data || response.data;
  }

  static async createRole(roleData: CreateRoleDto): Promise<Role> {
    const response = await httpClient.post<Role>(
      '/auth/roles',
      roleData
    );
    
    return (response.data as any).data || response.data;
  }

  static async updateRole(id: string, roleData: UpdateRoleDto): Promise<Role> {
    const response = await httpClient.put<Role>(
      `/auth/roles/${id}`,
      roleData
    );
    
    return (response.data as any).data || response.data;
  }

  static async deleteRole(id: string): Promise<void> {
    await httpClient.delete(`/auth/roles/${id}`);
  }

  static async toggleRoleStatus(id: string): Promise<Role> {
    const response = await httpClient.post<Role>(
      `/auth/roles/${id}/toggle-status`
    );
    
    return (response.data as any).data || response.data;
  }

  static async getAvailableModules(): Promise<Module[]> {
    const response = await httpClient.get<any>(
      '/auth/roles/modules'
    );

    // El backend devuelve directamente el array, envuelto por TransformInterceptor
    return (response.data as any).data || response.data || [];
  }

  static async getRoleModulePermissions(roleId: string, moduleId: string): Promise<Permission[]> {
    const response = await httpClient.get<any>(
      `/auth/roles/${roleId}/modules/${moduleId}/permissions`
    );
    
    const data = (response.data as any).data || response.data;
    return data?.permissions || [];
  }

  static async addPermissionToRoleModule(roleId: string, moduleId: string, permissionId: string): Promise<void> {
    await httpClient.post(
      `/auth/roles/${roleId}/modules/${moduleId}/permissions/${permissionId}`
    );
  }

  static async removePermissionFromRoleModule(roleId: string, moduleId: string, permissionId: string): Promise<void> {
    await httpClient.delete(
      `/auth/roles/${roleId}/modules/${moduleId}/permissions/${permissionId}`
    );
  }

  static async assignModuleToRole(roleId: string, moduleId: string): Promise<void> {
    await httpClient.post(`/auth/roles/${roleId}/modules/${moduleId}`);
  }

  static async removeModuleFromRole(roleId: string, moduleId: string): Promise<void> {
    await httpClient.delete(`/auth/roles/${roleId}/modules/${moduleId}`);
  }

  static async getAllPermissions(): Promise<Permission[]> {
    const response = await httpClient.get<{ data: Permission[] }>(
      '/auth/modules/permissions'
    );
    
    
    return response.data.data;
  }
}